/**
 * Rodapé. Porte de `bicicletas_peniel_prototipo.html` linhas 307-356.
 *
 * O `id="sobre"` fica aqui, como no protótipo — o link "Sobre Nós" da nav rola
 * até o rodapé, que é onde vive o texto sobre a loja.
 *
 * Correções sobre o original:
 *  - telefone, e-mail e endereço eram TEXTO INERTE. Agora são `tel:`,
 *    `mailto:` e link para o Maps — num site cuja conversão é ligar/mandar
 *    mensagem, telefone não clicável no celular é perda direta;
 *  - `hover:text-brand-400` não fazia nada, porque `brand-400` não existia na
 *    config. Agora existe;
 *  - os links sociais eram `href="#"`. Agora vêm de `Configuracao` e o item é
 *    OMITIDO quando vazio (a loja não informou Facebook), em vez de virar um
 *    link morto;
 *  - "Política de Garantia" apontava para `#` — removido, porque a página não
 *    existe. Link para lugar nenhum é pior que link ausente;
 *  - o ano do copyright era fixo em 2026.
 */
import { Link } from 'react-router-dom'

import { Icone } from '@/shared/ui/Icone.jsx'
import { useConfigLoja } from '@/shared/hooks/useConfigLoja.js'
import { LinkAncora } from './LinkAncora.jsx'

export function Rodape() {
  const config = useConfigLoja()
  const ano = new Date().getFullYear()

  /** wa.me só aceita dígitos; o `tel:` precisa do `+`. */
  const telHref = `+${config.whatsappNumero.replace(/\D/g, '')}`

  const sociais = [
    { url: config.instagramUrl, icone: 'instagram' as const, nome: 'Instagram' },
    { url: config.facebookUrl, icone: 'facebook' as const, nome: 'Facebook' },
  ].filter((s) => s.url !== '')

  return (
    <footer id="sobre" className="bg-gray-900 py-12 text-gray-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {/* ── marca + sobre + sociais ── */}
          <div>
            <Link to="/" className="mb-4 flex items-center gap-2">
              <Icone nome="bike" className="text-2xl text-brand-500" />
              <span className="text-xl font-bold tracking-tight text-white">
                Bicicletas <span className="text-brand-500">Peniel</span>
              </span>
            </Link>

            <p className="mb-4 text-sm text-gray-400">{config.sobreTexto}</p>

            {sociais.length > 0 && (
              <ul className="flex space-x-4">
                {sociais.map((social) => (
                  <li key={social.nome}>
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${social.nome} da Bicicletas Peniel (abre em nova aba)`}
                      className="text-xl text-gray-400 transition hover:text-white"
                    >
                      <Icone nome={social.icone} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── links rápidos ── */}
          <nav aria-label="Rodapé">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-white">
              Links Rápidos
            </h2>
            <ul className="space-y-2 text-sm">
              <li>
                <LinkAncora ancora="inicio" className="transition hover:text-brand-400">
                  Início
                </LinkAncora>
              </li>
              <li>
                <LinkAncora ancora="novas" className="transition hover:text-brand-400">
                  Bicicletas Novas
                </LinkAncora>
              </li>
              <li>
                <LinkAncora ancora="usadas" className="transition hover:text-brand-400">
                  Bicicletas Usadas
                </LinkAncora>
              </li>
              <li>
                <Link to="/bicicletas" className="transition hover:text-brand-400">
                  Ver todo o catálogo
                </Link>
              </li>
            </ul>
          </nav>

          {/* ── contato ── */}
          <div>
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-white">
              Contato
            </h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Icone nome="local" className="mt-1 text-brand-500" />
                {config.lojaMapsUrl ? (
                  <a
                    href={config.lojaMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition hover:text-brand-400"
                  >
                    {config.lojaEndereco}
                  </a>
                ) : (
                  <span>{config.lojaEndereco}</span>
                )}
              </li>

              <li className="flex items-center gap-3">
                <Icone nome="whatsapp" className="text-brand-500" />
                <a href={`tel:${telHref}`} className="transition hover:text-brand-400">
                  {config.lojaTelefoneExibicao}
                </a>
              </li>

              {config.lojaEmail && (
                <li className="flex items-center gap-3">
                  <Icone nome="email" className="text-brand-500" />
                  <a
                    href={`mailto:${config.lojaEmail}`}
                    className="transition hover:text-brand-400"
                  >
                    {config.lojaEmail}
                  </a>
                </li>
              )}

              {config.lojaHorario && (
                <li className="flex items-start gap-3 text-gray-400">
                  <span className="mt-0.5">{config.lojaHorario}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* gray-400 e não gray-500: sobre o gray-900 do rodapé o 500 dá 3,67:1 e
            reprova o AA. O 400 dá 6,82:1. É o espelho da regra do card vendido,
            onde sobre BRANCO acontece o inverso. */}
        <div className="mt-12 border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
          &copy; {ano} Bicicletas Peniel. Todos os direitos reservados.
        </div>
      </div>
    </footer>
  )
}
